// The 5 site-wide structured-data blocks. Homepage-only: injected here via Home.jsx (not in index.html
// head), so they no longer appear on every pre-rendered page. Content matches the original structured-data.html.

// One canonical definition of 99tests, reused verbatim in the page copy and the structured data.
export const DEFINITION = "99tests is a fixed-price testing service: real testers run your test cases, including AI-generated ones, on their own phones, laptops and browsers and send back a human-verified pass/fail report in 3 business days. It is built for teams shipping with AI coding tools that have test cases but no device lab or QA team. Prices start at $129, or ₹9,999 + GST in India, with no account or sales call.";

// Buyer-intent FAQs surfaced on the homepage; the visible text and this machine-readable copy are identical.
export const BUYER_FAQ_HOME = [
  ["What is 99tests?", "99tests is a crowdsourced software testing service: real people run your test cases on the real phones, laptops and browsers they own and send back a human-verified pass/fail report in 3 business days. Fixed price, paid online, no account or sales call."],
  ["Do I need an account or a sales call to use 99tests?", "No. You pay online, upload your test cases and a build link, and testers start within the hour — no account to create, no demo and no sales call. Starter and Standard sit below most engineering managers' card limits, so there's no procurement either."],
  ["Does 99tests charge per slot or by the hour?", "Per slot, fixed. A slot is one phone, laptop or browser; each package bundles a set number of slots with no hourly billing, no overage and no expenses. Need more? Add slots in blocks of five."],
];

const ORG = {
  "@context": "https://schema.org",
  "@type": ["Organization", "LocalBusiness"],
  "@id": "https://99tests.com/#org",
  name: "99tests",
  legalName: "Super Founder Hub",
  url: "https://99tests.com/",
  logo: "https://99tests.com/assets/og-99tests.png",
  image: "https://99tests.com/assets/og-99tests.png",
  description: DEFINITION,
  email: "hello@99tests.com",
  telephone: "+91-94483-10439",
  foundingDate: "2010",
  founder: { "@type": "Person", name: "Praveen Singh", jobTitle: "Founder", url: "https://linkedin.com/in/praveensinghv", sameAs: ["https://linkedin.com/in/praveensinghv"] },
  address: {
    "@type": "PostalAddress",
    streetAddress: "UKIBC, No 9, Prestige Blue Chip Tech Park, Hosur Main Road, Dairy Colony, Adugodi",
    addressLocality: "Bengaluru",
    addressRegion: "Karnataka",
    postalCode: "560029",
    addressCountry: "IN",
  },
  geo: { "@type": "GeoCoordinates", latitude: 12.9436, longitude: 77.6081 },
  areaServed: ["IN", "US", "GB", "SG", "AE"],
  priceRange: "₹9,999 – ₹34,999",
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "17:00",
  },
  sameAs: [
    "https://www.crunchbase.com/organization/99tests",
    "https://www.g2.com/products/99tests/reviews",
    "https://www.producthunt.com/products/99tests",
    "https://www.saashub.com/99tests",
    "https://www.linkedin.com/company/99tests/",
  ],
  knowsAbout: ["software testing", "crowdsourced testing", "mobile app testing", "cross-browser testing", "exploratory testing", "real device testing"],
};

const OFFER = (name, description, inr, usd) => ({
  "@type": "Offer",
  name,
  description,
  price: inr,
  priceCurrency: "INR",
  priceSpecification: [
    { "@type": "UnitPriceSpecification", price: inr, priceCurrency: "INR", valueAddedTaxIncluded: false },
    { "@type": "UnitPriceSpecification", price: usd, priceCurrency: "USD" },
  ],
  availability: "https://schema.org/InStock",
  url: "https://99tests.com/pricing",
  seller: { "@id": "https://99tests.com/#org" },
});

const SERVICE = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": "https://99tests.com/#service",
  name: "Real-device app and browser testing",
  serviceType: "Software testing",
  provider: { "@id": "https://99tests.com/#org" },
  areaServed: "Worldwide",
  description: "Human testers execute your test cases on real Android and iOS phones, Windows and macOS laptops, and Chrome, Safari, Edge and Firefox browsers. Every finding is verified, severity-graded and backed by a screen recording. Report in 3 business days.",
  termsOfService: "https://99tests.com/terms",
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "99tests packages",
    itemListElement: [
      OFFER("Starter", "5 sessions × 45 min, 2 devices or browsers, up to ~25 test cases, report in 3 business days.", "9999", "129"),
      OFFER("Standard", "10 sessions × 45 min, 4 devices or browsers, up to ~50 test cases, report in 3 business days.", "18999", "249"),
      OFFER("Deep", "20 sessions × 90 min, 6 devices or browsers, up to ~120 test cases, report in 5 business days.", "34999", "449"),
    ],
  },
};

// SoftwareApplication with the three packages as offers. Added so homepage and
// pricing expose an explicitly-typed SoftwareApplication/Offer block that strict
// AEO/GEO parsers detect (the Organization block uses an array @type some tools skip).
export const SOFTWARE = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": "https://99tests.com/#software",
  name: "99tests",
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "Software testing / QA",
  operatingSystem: "Web, Android, iOS, Windows, macOS",
  url: "https://99tests.com/",
  description: DEFINITION,
  provider: { "@id": "https://99tests.com/#org" },
  publisher: { "@id": "https://99tests.com/#org" },
  offers: {
    "@type": "AggregateOffer",
    priceCurrency: "INR",
    lowPrice: "9999",
    highPrice: "34999",
    offerCount: 3,
    offers: [
      OFFER("Starter", "5 sessions × 45 min, 2 devices or browsers, up to ~25 test cases, report in 3 business days.", "9999", "129"),
      OFFER("Standard", "10 sessions × 45 min, 4 devices or browsers, up to ~50 test cases, report in 3 business days.", "18999", "249"),
      OFFER("Deep", "20 sessions × 90 min, 6 devices or browsers, up to ~120 test cases, report in 5 business days.", "34999", "449"),
    ],
  },
};

const FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    ...BUYER_FAQ_HOME,
    ["What formats do you accept for test cases?", "CSV, Excel, Google Sheets, Word, Markdown, plain text, TestRail or Jira exports. AI-generated cases from any tool."],
    ["How do I share the build?", "APK link, Play internal testing, TestFlight, or a staging/production URL for web apps."],
    ["Which browsers and laptops do you cover?", "Chrome, Safari, Edge and Firefox on Windows, macOS and ChromeOS on testers' own machines."],
    ["Who chooses the devices?", "You set targets; we match testers who own them. Unfilled sessions are credited back."],
    ["Is my app confidential?", "Testers accept confidentiality terms; builds are shared only with assigned testers and not stored beyond the job."],
    ["Do you invoice with GST?", "Yes, for Indian customers via Stripe. Others are billed in USD."],
    ["Do you test on real devices or emulators?", "Real devices only. Every session runs on a phone, laptop or browser the tester personally owns — Redmi, Realme, Galaxy, Vivo, iPhone, Chrome and Edge on Windows, Safari on macOS. Emulators miss what MIUI, Jio networks and Safari do in the wild."],
    ["How long does a report take?", "Starter and Standard packages deliver in 3 business days; Deep delivers in 5. The report arrives as a web page and PDF."],
  ].map(([name, text]) => ({ "@type": "Question", name, acceptedAnswer: { "@type": "Answer", text } })),
};

const HOWTO = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "From payment to verified test report in three business days",
  description: "How a 99tests real-device testing job runs, from checkout to delivered report.",
  totalTime: "P3D",
  estimatedCost: { "@type": "MonetaryAmount", currency: "INR", value: "9999" },
  step: [
    ["Pay", "Choose a package, pay by card, invoice at checkout."],
    ["Submit", "Share a build link or URL, test cases in any format, and device targets."],
    ["Plan", "Cases are parsed into sessions by device."],
    ["Dispatch", "Offers go to testers who own your target devices."],
    ["Sessions", "Timed 45 or 90 minutes, findings recorded live with media."],
    ["Verify and deliver", "Findings checked, merged, graded; report delivered as page and PDF."],
  ].map(([name, text], i) => ({ "@type": "HowToStep", position: i + 1, name, text })),
};

const WEBSITE = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://99tests.com/#website",
  url: "https://99tests.com/",
  name: "99tests",
  publisher: { "@id": "https://99tests.com/#org" },
  inLanguage: "en-IN",
};

export const HOME_JSONLD = [SOFTWARE, SERVICE, FAQ, HOWTO, WEBSITE];

// Organization structured data reused on the /about page.
export const ABOUT_JSONLD = [ORG];
